import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { apiMessageTemplateById } from "helpers/url/api/v2";
import { MessageTemplate } from "./models";

import { sharedTemplateKeys } from "../SharedTemplate/hooks";
import { toCreateMessageTemplatePayload, toUpdateMessageTemplatePayload } from "./helpers";
import { MessageTemplateSchema } from "./schema";

export const messageTemplateKeys = {
  all: ["messageTemplates"] as const,
  lists: () => [...messageTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...messageTemplateKeys.lists(), { filters }] as const,
  details: () => [...messageTemplateKeys.all, "detail"] as const,
  detail: (id: string) => [...messageTemplateKeys.details(), id] as const,
};

export function setNewMessageTemplateInQueryClient(queryClient: QueryClient, newMessageTemplate: MessageTemplate) {
  queryClient.setQueryData(messageTemplateKeys.detail(newMessageTemplate.id), (oldData: MessageTemplate | undefined) => {
    if (!oldData) {
      return newMessageTemplate;
    }
    return {
      ...oldData,
      ...newMessageTemplate,
    };
  });
}

export const useMessageTemplateDetailQuery = (id: string | undefined, options = {}) => useQuery<MessageTemplate | null>({
  queryKey: messageTemplateKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiMessageTemplateById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useMessageTemplateListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<MessageTemplate[]>({
    queryKey: queryString ? messageTemplateKeys.list(queryString) : messageTemplateKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.messageTemplates.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

function useMessageTemplateCreateRequest(mutationFn: (data: MessageTemplateSchema) => Promise<MessageTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: messageTemplateKeys.all });
      setNewMessageTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

function useMessageTemplateUpdateRequest(mutationFn: (data: Partial<MessageTemplateSchema>) => Promise<MessageTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: messageTemplateKeys.all });
      setNewMessageTemplateInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateMessageTemplate(messageTemplateId: string) {
  return useMessageTemplateUpdateRequest(
    async (formData: Partial<MessageTemplateSchema>): Promise<MessageTemplate> => {
      const payload = toUpdateMessageTemplatePayload(formData);
      return apiClient.put<MessageTemplate>(apiMessageTemplateById(messageTemplateId), { messageTemplate: payload });
    },
  );
}

export function useCreateMessageTemplate() {
  return useMessageTemplateCreateRequest(
    async (formData: MessageTemplateSchema): Promise<MessageTemplate> => {
      const payload = toCreateMessageTemplatePayload(formData);
      return apiClient.post<MessageTemplate>(API_ROUTES.messageTemplates.base, { messageTemplate: payload });
    },
  );
}
